import i18next from "i18next";

interface TutorialData {
  text: string[];
  arrow?: {
    tileX?: number;
    tileY?: number;
    screenX?: number;
    screenY?: number;
  };
}

interface TutorialState {
  tutorials: { [key: string]: TutorialData };
  seenTutorials: string[];
}

class TutorialHandler {
  private tutorialState: TutorialState = {
    tutorials: {},
    seenTutorials: [],
  };

  constructor() {
    this.loadTutorials();
  }

  /**
   * Load tutorials from the data file
   */
  private loadTutorials() {
    // In a real implementation, we'd fetch from the tutorials.json file
    // For now, we initialize with an empty state that will be populated
    // when account data is received from the server
  }

  /**
   * Initialize tutorial handler with data from server
   * This is called when player-data is received with the tutorial array
   */
  initializeWithAccountData(seenTutorials: string[] = []) {
    this.tutorialState.seenTutorials = Array.isArray(seenTutorials)
      ? seenTutorials
      : [];
  }

  /**
   * Load all tutorials from the localized tutorials.json file
   */
  async loadTutorialsFromFile(): Promise<void> {
    try {
      const currentLang = i18next.language || "en";
      const response = await fetch(`/locales/${currentLang}/tutorials.json`);
      if (!response.ok) {
        throw new Error(`Failed to fetch tutorials.json: ${response.status}`);
      }
      const tutorialsData = await response.json();
      this.tutorialState.tutorials = tutorialsData;
    } catch (error) {
      console.error("[TutorialHandler] Error loading tutorials:", error);
    }
  }

  /**
   * Check if a tutorial has been seen by the user
   */
  hasSeen(tutorialKey: string): boolean {
    const seen = this.tutorialState.seenTutorials.includes(tutorialKey);
    return seen;
  }

  /**
   * Get a tutorial by its key
   */
  getTutorial(tutorialKey: string): TutorialData | null {
    const tutorial = this.tutorialState.tutorials[tutorialKey] || null;
    return tutorial;
  }

  /**
   * Check if a tutorial should be shown (exists and hasn't been seen)
   */
  shouldShow(tutorialKey: string): boolean {
    const should =
      !this.hasSeen(tutorialKey) && !!this.getTutorial(tutorialKey);
    return should;
  }

  /**
   * Mark a tutorial as seen
   */
  markAsSeen(tutorialKey: string) {
    if (!this.tutorialState.seenTutorials.includes(tutorialKey)) {
      this.tutorialState.seenTutorials.push(tutorialKey);
    }
  }

  /**
   * Get the current list of seen tutorials
   */
  getSeenTutorials(): string[] {
    return [...this.tutorialState.seenTutorials];
  }

  /**
   * Get all tutorials
   */
  getAllTutorials(): { [key: string]: TutorialData } {
    return { ...this.tutorialState.tutorials };
  }
}

// Export singleton instance
export const tutorialHandler = new TutorialHandler();
